import React, { useEffect, useRef, memo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActionIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useWindowSize, useDebounce } from '@uidotdev/usehooks';
import { TbCopy } from 'react-icons/tb';
import canvasDatagrid from 'canvas-datagrid';
import { CANVAS_TABLE_STYLES, IMAGE_TYPES, SPECIAL_NULL_STRING } from './constants';
import * as eventUtils from '../../../../utils/event.utils';
import { formatCellNumber } from '../../../../utils/formatCellNumber';

const DEFAULT_CELL_FONT_SIZE = 12;

interface SelectedCell {
  rowId: number;
  columnId: number;
  text: unknown;
}

interface CanvasTableProps {
  data: unknown[];
  foundCell?: { rowIndex: number; columnIndex: number } | null;
  zoomLevel: number;
  startLine?: number;
  endLine?: number;
  getCellStyle?: (
    row: number,
    col: number
  ) => { background?: string; borderColor?: string } | undefined;
  onFocusedCell: (cell: SelectedCell) => void;
  setSelectedCells: (cells: SelectedCell[]) => void;
  handleSelection: (cells: SelectedCell[]) => void;
  style?: React.CSSProperties;
  rowPinning: { top: unknown[] };
  columnPinning: { left: unknown[] };
  isStructured?: boolean;
  className?: string;
  scrolledPosition: { top: number; left: number };
  onChangeScrollPosition?: (event: unknown) => void;
  width?: number;
  columnsTypes?: Record<string, string>;
}

// Don't use toast.error in the canvas table because toast library behaves strange with the canvas
// and causes unexpected re-renders of the whole application
const OutputCanvasTable = memo(
  ({
    data,
    foundCell,
    zoomLevel,
    startLine,
    endLine,
    getCellStyle,
    onFocusedCell,
    setSelectedCells,
    handleSelection,
    style,
    rowPinning,
    columnPinning,
    isStructured,
    className,
    key,
    scrolledPosition,
    onChangeScrollPosition,
    width,
    columnsTypes
  }: CanvasTableProps & { key?: string }) => {
    const [searchParams] = useSearchParams();
    const isTransformationTab = searchParams?.get('tab') === 'transformation';
    const element = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
    const startLineRef = useRef<number | undefined>(startLine);
    startLineRef.current = startLine;
    const endLineRef = useRef<number | undefined>(endLine);
    endLineRef.current = endLine;

    const onFocusedCellRef = useRef(onFocusedCell);
    onFocusedCellRef.current = onFocusedCell;

    const setSelectedCellsRef = useRef(setSelectedCells);
    setSelectedCellsRef.current = setSelectedCells;

    const handleSelectionRef = useRef(handleSelection);
    handleSelectionRef.current = handleSelection;

    const getCellStyleRef = useRef(getCellStyle);
    getCellStyleRef.current = getCellStyle;

    // Hover-anchored copy button — one floating overlay per grid, not per cell.
    const [hoverCopy, setHoverCopy] = useState<{
      top: number;
      left: number;
      value: unknown;
    } | null>(null);

    const onChangeScrollPositionRef = useRef(onChangeScrollPosition);
    onChangeScrollPositionRef.current = onChangeScrollPosition;

    const multiSelectCellsRef = useRef([]);

    const [grid, setGrid] = useState<any>(null);
    // Ref-mirror of the latest scroll position so it survives across renders
    // without forcing the data-sync effect to depend on the prop reference.
    const scrolledPositionRef = useRef(scrolledPosition);
    scrolledPositionRef.current = scrolledPosition;

    const windowSize = useWindowSize();
    const debouncedWindowSize = useDebounce(windowSize, 100);

    const updateStyles = useCallback(
      grid => {
        if (!grid) return;

        const fontSize = DEFAULT_CELL_FONT_SIZE * zoomLevel;
        const cellHeight = DEFAULT_CELL_FONT_SIZE * 2 * zoomLevel;

        const font = `${fontSize}px 'Gilroy',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    sans-serif`;
        // Capture scroll BEFORE the style writes — canvas-datagrid recomputes
        // its layout on each style assignment and any of these can clamp
        // scrollTop/scrollLeft back to 0. Restoring after the writes keeps
        // the user's scroll position across font/zoom/size churns.
        const prevTop = grid.scrollTop ?? 0;
        const prevLeft = grid.scrollLeft ?? 0;

        grid.style.cellFont = font;
        grid.style.columnHeaderCellFont = font;
        grid.style.rowHeaderCellFont = font;
        grid.style.activeCellFont = font;
        grid.style.cellHeight = cellHeight;
        grid.style.columnHeaderCellHeight = cellHeight;
        const rect = element.current.getBoundingClientRect();
        const nextWidth = rect.width + 'px';
        const nextHeight = rect.height + 'px';
        if (grid.style.width !== nextWidth) grid.style.width = nextWidth;
        if (grid.style.height !== nextHeight) grid.style.height = nextHeight;

        try {
          grid.scrollTop = prevTop;
          grid.scrollLeft = prevLeft;
        } catch (_) {
          /* swallow */
        }
      },
      [grid, zoomLevel, element]
    );

    useEffect(() => {
      if (!element.current) return;

      const grid = canvasDatagrid({
        allowFreezingColumns: true,
        allowFreezingRows: true, // freezing line appears on top-left corner of the table
        showFilter: true, // filter option in the context menu (appears when you right-click on the table)
        showClearSettingsOption: true, // hides clear settings in the context menu options
        parentNode: element.current,
        editable: false,
        allowSorting: false,
        maxPixelRatio: 3, // by default 1.5 (1920x1080), 3 for (4K screens)
        showPerformance: false, // if set to "true" shows graphical chart of the performance
        selectionFollowsActiveCell: true, // otherwise active and selected cells are different
        keepFocusOnMouseOut: true // grid will continue to handle keydown events when mouse is outside the grid
        // selectionFollowsActiveCell: true,
        // allowRowResizeFromCell: true, // resizing each cell
        // borderDragBehavior: 'resize',
        // singleSelectionMode: true, // no effect so far
        // selectionHandleBehavior: 'ismoil', // no effect so far
      });

      grid.style.width = element.current.getBoundingClientRect().width + 'px';
      grid.style.height = element.current.getBoundingClientRect().height + 'px';
      // grid.style.columnHeaderCellBackgroundColor = '#eef0f4';
      // grid.style.rowHeaderCellBackgroundColor = '#eef0f4';
      grid.style.columnHeaderCellHorizontalAlignment = 'center';
      grid.style.rowHeaderCellHorizontalAlignment = 'center';
      // grid.style.activeCellBorderColor = 'red';
      // grid.style.activeCellBorderWidth = 3;
      grid.style.activeCellOverlayBorderWidth = 1.5;
      // The cell highlighted when picking an error/warning in the sidebar is a
      // `selectArea` selection, drawn with the selection overlay (default width
      // 1) — thicken it so the jumped-to cell reads clearly.
      grid.style.selectionOverlayBorderWidth = 2.5;
      // Hide canvas-datagrid's own row-number text across every state variant.
      // Its built-in horizontal alignment is applied inconsistently between the
      // active row and the rest in this build, so we take over the gutter and
      // repaint each number ourselves (in `afterrendercell`) at one identical
      // position. Spraying every plausible colour key covers whichever one this
      // build uses for the active / selected / hovered row header.
      const ROW_HEADER_COLOR_KEYS = [
        'rowHeaderCellColor',
        'rowHeaderCellHoverColor',
        'rowHeaderCellSelectedColor',
        'rowHeaderCellActiveColor',
        'activeRowHeaderCellColor',
        'selectedRowHeaderCellColor',
        'rowSelectorCellColor',
        'rowSelectorCellSelectedColor',
        'rowSelectorCellActiveColor',
        'activeCellRowHeaderColor',
        'selectedHeaderCellColor'
      ];
      for (const colorKey of ROW_HEADER_COLOR_KEYS) {
        try {
          grid.style[colorKey] = 'rgba(0, 0, 0, 0)';
        } catch (_) {
          /* swallow */
        }
      }
      updateStyles(grid);
      // grid.style.editCellBackgroundColor = 'red';
      // grid.style.groupIndicatorBackgroundColor = 'red';
      // grid.style.cellSelectedBackgroundColor = 'red';

      const handleGridTableCopy = () => {
        const selectedCells = grid.selectedCells || [];
        if (!selectedCells.length) return;

        try {
          let formattedData = '';
          const validCells = selectedCells.filter(cell => cell !== null);
          const rows = validCells
            .map(cell => {
              const values = Object.values(cell);
              return values.join('\t');
            })
            .filter(row => row.trim() !== '');
          formattedData = rows.join('\n');
          navigator.clipboard.writeText(formattedData).catch(error => {
            console.log(`handleGridTableCopy ${error}`);
          });
        } catch (error) {
          console.log(`handleGridTableCopy ${error}`);
        }
      };

      const unhideAllColumns = () => {
        grid.schema.forEach(col => {
          col.hidden = false;
        });

        grid.draw();
      };

      const originalFilter = grid.filters.string;

      grid.filters.string = function (cellText, searchText) {
        try {
          // In transformation - first row=0 is always a header row, so we never filter out the header row
          // In extraction - we don't know which row is a header row, in this case the "mouse focused row" will not be filtered out
          let currentCellValues = Object.values(grid.currentCell.data);
          if (isTransformationTab) {
            currentCellValues = Object.values(grid.data[0]);
          }
          if (currentCellValues.includes(cellText)) return true;
          return originalFilter(cellText, searchText);
        } catch (error) {
          console.log(`grid.filters ${error}`);
          return originalFilter(cellText, searchText);
        }
      };

      // Border colour for the cell currently being drawn. `rendercell` already
      // calls getCellStyle() once per cell for the background; it stashes the
      // border here so `afterrendercell` can paint the frame without a second
      // getCellStyle() call. canvas-datagrid fires rendercell → afterrendercell
      // back-to-back for the same cell, so this stays correctly scoped.
      let pendingBorderColor: string | null = null;

      // after the cell is rendered, draw on top of it
      grid.addEventListener('afterrendercell', function (e) {
        // Row-number gutter (columnIndex === -1): paint every row number
        // ourselves, centered identically, since native alignment is hidden
        // above. rowIndex >= 0 skips the top-left corner cell (rowIndex -1).
        if (e.cell.columnIndex === -1 && e.cell.rowIndex >= 0) {
          try {
            const ctx = e.ctx;
            const { x, y, width, height } = e.cell;
            const label = String(e.cell.value ?? e.cell.rowIndex + 1);
            ctx.save();
            ctx.font = grid.style.rowHeaderCellFont;
            ctx.fillStyle = '#495057';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, x + width / 2, y + height / 2 + 1);
            ctx.restore();
          } catch (err) {
            console.log(`afterrendercell row-number ${err}`);
          }
          return;
        }

        if (e.cell.rowIndex === 0 && e.cell.columnIndex !== -1 && columnsTypes) {
          const i = IMAGE_TYPES?.[columnsTypes[e.cell.columnIndex]]?.();

          if (i && i?.width !== 0) {
            i.targetHeight = e.cell.height;
            i.targetWidth = e.cell.height * (i?.width / i?.height);
            e.ctx.drawImage(i, e.cell.x + 4, e.cell.y + 4, 16, 16);
          }
        }

        // Dashed cell outline driven by getCellStyle().borderColor (stashed by
        // rendercell). Painted here so it sits on top of the cell's fill and
        // text. The Output Tables view uses this to frame the whole column of a
        // selected error / warning / mandatory issue; consumers that don't
        // return a borderColor (e.g. extraction) leave pendingBorderColor null
        // and are unaffected.
        try {
          const borderColor = pendingBorderColor;
          pendingBorderColor = null;

          if (borderColor && e.cell.rowIndex >= 0 && e.cell.columnIndex >= 0) {
            const { x, y, width, height } = e.cell;
            const ctx = e.ctx;
            // Inset the strokes so the left/right lines sit fully inside this
            // cell. Drawing them exactly on the cell boundary lets the
            // neighbouring column (painted right after) repaint over the shared
            // edge and erase the line — most visibly the right border.
            const inset = 1.5;
            ctx.save();
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 3]);
            ctx.beginPath();
            ctx.moveTo(x + inset, y);
            ctx.lineTo(x + inset, y + height);
            ctx.moveTo(x + width - inset, y);
            ctx.lineTo(x + width - inset, y + height);
            if (e.cell.rowIndex === 0) {
              ctx.moveTo(x + inset, y + inset);
              ctx.lineTo(x + width - inset, y + inset);
            }
            ctx.stroke();
            ctx.restore();
          }
        } catch (err) {
          console.log(`afterrendercell border ${err}`);
        }
      });

      grid.addEventListener('rendercell', function (e) {
        // Cleared each cell so a stale border from a previous cell can't leak
        // into one whose getCellStyle() returns no borderColor.
        pendingBorderColor = null;
        try {
          const isHeaderRow = e.cell.rowIndex === startLineRef.current! - 1;
          const isFooterRow = e.cell.rowIndex === endLineRef.current! - 1;

          if (e.cell.rowIndex === 0 && e.cell.columnIndex !== -1 && columnsTypes) {
            const ctx = e.ctx;
            const { x, y, height } = e.cell;

            ctx.fillText(e.cell.value, x + 26, y + height / 2);
          }

          if (isHeaderRow && isStructured) {
            e.ctx.fillStyle = CANVAS_TABLE_STYLES.COLORS.LIGHT_BLUE;
          } else if (isFooterRow && isStructured) {
            e.ctx.fillStyle = CANVAS_TABLE_STYLES.COLORS.LIGHT_BLUE;
            e.ctx.strokeStyle = CANVAS_TABLE_STYLES.COLORS.BLACK;
            e.ctx.lineWidth = 1.25;
          } else if (getCellStyleRef.current) {
            const cellStyle = getCellStyleRef.current(e.cell.rowIndex + 2, e.cell.columnIndex + 1);
            pendingBorderColor = cellStyle?.borderColor ?? null;
            const cellBackground = cellStyle?.background;
            if (cellBackground && cellBackground !== '#fff') {
              e.ctx.fillStyle = cellBackground;
            }
          }
        } catch (error) {
          console.log(`rendercell ${error}`);
        }
      });

      function isQuotedNumber(value) {
        if (typeof value !== 'string') return false;
        const trimmed = value.trim();
        if (trimmed.length < 2) return false;
        const isWrapped =
          (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
          (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (!isWrapped) return false;

        const unquotedValue = trimmed.slice(1, -1);
        return !isNaN(Number(unquotedValue)) && unquotedValue !== '';
      }

      grid.addEventListener('rendertext', function (e) {
        try {
          if (isQuotedNumber(e.cell.value)) {
            const fixedText = `"${e.cell.value}"`;
            e.ctx.fillText(fixedText, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          }
          if (
            e.cell.type === 'string' &&
            e.cell.value?.includes?.(' - ') &&
            e.cell.rowIndex !== 0
          ) {
            const fixedText = e.cell.value.replace(/ - /g, ' - ');
            e.ctx.fillText(fixedText, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          } else if (e.cell.rowIndex === 0 && e.cell.columnIndex !== -1 && columnsTypes) {
            e.ctx.fillText(e.cell.value, e.cell.x + 26, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          }

          // Thousand-separator render for plain numeric data-row cells.
          // Underlying value untouched — copy / filter / search still see raw.
          if (e.cell.rowIndex !== 0) {
            const formatted = formatCellNumber(e.cell.value);
            if (formatted !== null) {
              e.ctx.fillText(formatted, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
              e.preventDefault();
              return;
            }
          }
        } catch (err) {
          console.log(`rendertext ${err}`);
        }

        if (e.cell.value === SPECIAL_NULL_STRING) {
          e.ctx.fillStyle = '#aaa';
        }
      });

      grid.addEventListener('click', function (event) {
        try {
          const selectionResult = {
            rowId: event.cell.rowIndex + 1,
            columnId: event.cell.columnIndex + 1,
            text: event.cell.value
          };

          onFocusedCellRef.current(selectionResult);
          setSelectedCellsRef.current([selectionResult]);
          handleSelectionRef.current([selectionResult]);
        } catch (error) {
          console.log(`click ${error}`);
        }
      });

      grid.addEventListener('selectionchanged', event => {
        if (Array.isArray(event.selectedCells) && event.selectedCells.length > 0) {
          const seenRows = new Set(); // each row can only have 1 selection
          const formattedCells = event.selectedCells
            .filter(cell => {
              const rowId = cell.viewRowIndex;
              if (seenRows.has(rowId)) return false;
              seenRows.add(rowId);
              return true;
            })
            .map(cell => ({
              rowId: cell.viewRowIndex + 1,
              columnId: cell.viewColumnIndex + 1,
              text: cell.value
            }));

          if (!isStructured) multiSelectCellsRef.current = formattedCells;
        }
      });

      // Hover-anchored copy button — track the cell under the cursor and
      // surface a small overlay button at its right edge. Single DOM node
      // anchored via state — no per-cell DOM, no extra canvas draws.
      grid.addEventListener('mousemove', event => {
        try {
          const cell = event.cell;
          if (
            !cell ||
            cell.rowIndex == null ||
            cell.rowIndex < 0 ||
            cell.columnIndex == null ||
            cell.columnIndex < 0 ||
            cell.rowIndex === 0
          ) {
            setHoverCopy(prev => (prev === null ? prev : null));
            return;
          }
          const top = cell.y + 2;
          const left = cell.x + cell.width - 22;
          setHoverCopy(prev =>
            prev && prev.top === top && prev.left === left && prev.value === cell.value
              ? prev
              : { top, left, value: cell.value }
          );
        } catch (err) {
          console.log(`hover-copy mousemove ${err}`);
        }
      });

      // 'mouseup' event is also used for multi selection, it works in a similar way to 'keyup' event
      grid.addEventListener('mouseup', event => {
        const nativeEvent = event.NativeEvent;
        // don't select multiple cells if ctrl/cmd is pressed
        if (eventUtils.isCommandKey(nativeEvent)) return;

        const isMultiSelect =
          Array.isArray(multiSelectCellsRef.current) && multiSelectCellsRef.current.length > 1;

        if (isMultiSelect) {
          handleSelectionRef.current(multiSelectCellsRef.current);
          multiSelectCellsRef.current = [];
        }
      });

      // 'keydown' is used to copy selected cell values to clipboard
      grid.addEventListener('keydown', event => {
        const nativeEvent = event.NativeEvent;
        if (eventUtils.isCommandKey(nativeEvent) && nativeEvent.key === 'c') {
          handleGridTableCopy();
        }
      });

      // used for vertical multi selection using 'shift' key
      grid.addEventListener('keyup', event => {
        const nativeEvent = event.NativeEvent;
        if (nativeEvent.keyCode === 16) {
          const gridSelectedCells = Array.isArray(grid.selectedCells) ? grid.selectedCells : [];
          const formattedCells = gridSelectedCells
            .map((item, rowIndex) => {
              if (item && typeof item === 'object') {
                const columnKey = Object.keys(item)[0];
                return {
                  rowId: rowIndex + 1,
                  columnId: parseInt(columnKey) + 1,
                  text: item[columnKey]
                };
              }
              return null;
            })
            .filter(Boolean);

          if (formattedCells?.length > 1) {
            handleSelectionRef.current(formattedCells);
          }
        }
      });

      grid.addEventListener('contextmenu', e => {
        if (!e.items) return;

        if (e.cell && e.cell.header) {
          e.items.push({
            title: 'Unhide All Columns',
            click: () => {
              unhideAllColumns();
            }
          });
        }
      });

      const handleGridScroll = event => {
        try {
          onChangeScrollPositionRef.current?.(event);
          // Hide the hover-copy overlay during scroll — the cell rect moves,
          // and the next mousemove will reposition it cleanly.
          setHoverCopy(prev => (prev === null ? prev : null));
        } catch (error) {
          console.log(`onChangeScrollPosition ${error}`);
        }
      };

      const handleDocumentKeydown = event => {
        try {
          // used to close popup menu on `esc` key
          if (event.key === 'Escape') {
            grid?.disposeContextMenu?.();
            // disposeContextMenu removes the parent popup, but search suggestions still remain open
            // so we have to come up with custom element removal
            document.querySelectorAll('.canvas-datagrid-context-menu').forEach(el => {
              el.remove();
            });
          }
        } catch (err) {
          console.log(`Canvas table keydown event err`, err);
        }
      };

      document.addEventListener('keydown', handleDocumentKeydown);
      grid.addEventListener('scroll', handleGridScroll);

      // Initial data assignment + scroll restore. Subsequent `data` changes
      // are handled by the dedicated data-sync effect below, so the grid
      // instance is no longer torn down + recreated when the parent passes
      // a new array reference.
      grid.data = data;

      try {
        grid.scrollTop = scrolledPosition.top;
        grid.scrollLeft = scrolledPosition.left;
      } catch (err) {
        console.log(err);

        grid.scrollTop = 0;
        grid.scrollLeft = 0;
      }

      setGrid(grid);

      return () => {
        document.removeEventListener('keydown', handleDocumentKeydown);
        grid.removeEventListener('scroll', handleGridScroll);
        grid.dispose();
      };
      // `data` and `scrolledPosition` are intentionally NOT in the deps —
      // we don't want to dispose/recreate the grid (which resets scroll)
      // on every new `data` array reference. The data-sync effect below
      // handles in-place data updates instead.
    }, [isStructured, columnsTypes]);

    // Sync `data` into the existing grid instance without recreating it.
    // canvas-datagrid snaps scroll to (0, 0) when `grid.data =` is assigned,
    // so we (a) skip the assignment entirely when the new array has the same
    // content as the current one (most common case on prompt-save refetches),
    // and (b) restore scroll synchronously + rAF + setTimeout so whichever
    // timing canvas-datagrid finishes its internal recompute by, our restore
    // wins.
    useEffect(() => {
      if (!grid) return;
      if (grid.data === data) return;

      // Content-equality short-circuit: if the new array has the same length
      // and the same first/last row references as the current grid data, the
      // underlying spreadsheet content didn't change (React Query handed us
      // a fresh reference after a refetch but the rows are the same objects).
      // Skipping the assignment leaves canvas-datagrid's scroll untouched.
      const oldData = grid.data;
      if (
        Array.isArray(oldData) &&
        Array.isArray(data) &&
        oldData.length === data.length &&
        oldData.length > 0 &&
        oldData[0] === data[0] &&
        oldData[oldData.length - 1] === data[data.length - 1]
      ) {
        return;
      }

      const prevTop = grid.scrollTop ?? scrolledPositionRef.current?.top ?? 0;
      const prevLeft = grid.scrollLeft ?? scrolledPositionRef.current?.left ?? 0;
      // `visibleRows` is the row-index array canvas-datagrid keeps in sync
      // with the current viewport. Capturing the first visible row index
      // lets us re-anchor by semantic position (row N) instead of by raw
      // pixel scroll, which survives the library's internal scroll-reset
      // on `grid.data =`.
      const prevFirstVisibleRow = Array.isArray(grid.visibleRows) ? grid.visibleRows[0] : null;

      if (typeof grid.selectNone === 'function') {
        grid.selectNone(true);
      }

      grid.data = data;

      const restore = () => {
        try {
          // Prefer `gotoCell` (semantic: "show this row index at the top")
          // — it survives recompute cycles where the pixel-based
          // scrollTop/scrollLeft gets clamped to 0.
          if (
            prevFirstVisibleRow != null &&
            typeof grid.gotoCell === 'function' &&
            data &&
            prevFirstVisibleRow < data.length
          ) {
            grid.gotoCell(0, prevFirstVisibleRow, 0, 0);
          } else {
            grid.scrollTop = prevTop;
          }
          grid.scrollLeft = prevLeft;
        } catch (_) {
          // canvas-datagrid can throw when restoring to an out-of-range
          // position (new data shorter than previous scroll). Fall back to
          // 0/0 — better than leaving the grid in an undefined state.
          try {
            grid.scrollTop = 0;
            grid.scrollLeft = 0;
          } catch (__) {
            /* swallow */
          }
        }
      };

      // Restore at three points: synchronously (in case canvas-datagrid
      // hasn't recomputed yet and our write sticks), on the next animation
      // frame (after the library's typical internal recompute), and on a
      // macrotask (last resort for libraries that defer past rAF).
      restore();
      const frame = requestAnimationFrame(restore);
      const timer = window.setTimeout(restore, 0);

      return () => {
        cancelAnimationFrame(frame);
        window.clearTimeout(timer);
      };
    }, [data, grid]);

    useEffect(() => {
      function gotoCell() {
        try {
          grid.selectArea({
            top: foundCell!.rowIndex - 1,
            left: foundCell!.columnIndex - 1
          });
          grid.gotoCell(foundCell!.columnIndex - 1, foundCell!.rowIndex - 1, 0.2, 0.2);
        } catch (err) {
          console.log(`gotoCell ${err}`);
        }
      }

      if (foundCell && grid) {
        gotoCell();
      }
    }, [foundCell, grid]);

    useEffect(() => {
      updateStyles(grid);
      grid?.draw();
    }, [grid, zoomLevel, startLine, endLine]);

    const handlePinning = useCallback(
      (pinningLength, visibleSize, frozenKey) => {
        if (pinningLength > 0) {
          if (visibleSize >= pinningLength) {
            grid[frozenKey] = pinningLength;
          } else {
            if (!isTransformationTab) {
              // @ts-ignore
              notifications.show({
                color: 'orange',
                title:
                  'Cannot freeze panes — too few visible rows/columns. Open the table in full screen and retry.',
                position: 'bottom-center',
                autoClose: 3500
              });
            }
            console.warn('Unable to freeze panes - not enough visible rows/columns to support it', {
              pinningLength,
              visibleSize,
              frozenKey
            });
          }
        } else {
          grid[frozenKey] = 0;
        }
      },
      [grid]
    );

    useEffect(() => {
      if (!grid || !element.current || typeof ResizeObserver === 'undefined') return;

      // Reassigning `grid.style.width`/`height` triggers canvas-datagrid's
      // internal layout recompute, which can clamp the scroll position back
      // to (0, 0). To preserve scroll on container size changes (e.g. a
      // popover closing + sidebar reflow on prompt save), we (a) skip the
      // assignment when dimensions actually didn't change, and (b) capture
      // + restore scroll around any size change that does happen.
      const applySize = () => {
        if (!element.current) return;
        const rect = element.current.getBoundingClientRect();
        const nextWidth = rect.width + 'px';
        const nextHeight = rect.height + 'px';
        if (grid.style.width === nextWidth && grid.style.height === nextHeight) return;

        const prevTop = grid.scrollTop ?? 0;
        const prevLeft = grid.scrollLeft ?? 0;
        grid.style.width = nextWidth;
        grid.style.height = nextHeight;
        grid.draw?.();
        try {
          grid.scrollTop = prevTop;
          grid.scrollLeft = prevLeft;
        } catch (_) {
          /* swallow */
        }
      };

      const observer = new ResizeObserver(applySize);
      observer.observe(element.current);

      return () => {
        observer.disconnect();
      };
    }, [grid]);

    useEffect(() => {
      if (!grid) return;

      const visibleRowsSize = grid.visibleRows.length - 5;
      const visibleColumnsSize = grid.visibleRows.length - 5;

      handlePinning(rowPinning.top.length, visibleRowsSize, 'frozenRow');
      handlePinning(columnPinning.left.length, visibleColumnsSize, 'frozenColumn');
    }, [rowPinning, columnPinning, grid]);

    useEffect(() => {
      if (!grid) return;

      // Same scroll-preservation pattern as the ResizeObserver above —
      // window resizes / `width` prop churns must not reset the user's
      // scroll position.
      const rect = element.current.getBoundingClientRect();
      const nextWidth = rect.width + 'px';
      const nextHeight = rect.height + 'px';
      if (grid.style.width === nextWidth && grid.style.height === nextHeight) return;

      const prevTop = grid.scrollTop ?? 0;
      const prevLeft = grid.scrollLeft ?? 0;
      grid.style.width = nextWidth;
      grid.style.height = nextHeight;
      try {
        grid.scrollTop = prevTop;
        grid.scrollLeft = prevLeft;
      } catch (_) {
        /* swallow */
      }
    }, [debouncedWindowSize, grid, style, width]);

    useEffect(() => {
      if (element.current) {
        const preventDefault = e => {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        };
        element.current.addEventListener('wheel', preventDefault, {
          passive: false
        });
        element.current.addEventListener('touchmove', preventDefault, {
          passive: false
        });
        return () => {
          element.current?.removeEventListener('wheel', preventDefault);
          element.current?.removeEventListener('touchmove', preventDefault);
        };
      }
    }, [element]);

    useEffect(() => {
      if (!grid) return;

      grid.draw();
    }, [getCellStyle, grid]);

    return (
      <div
        id={key}
        key={key}
        className={className}
        style={{
          ...style,
          height: '100%',
          overscrollBehavior: 'none',
          position: 'relative'
        }}
        onMouseLeave={() => setHoverCopy(null)}
        ref={element}
      >
        {hoverCopy && (
          <Tooltip label="Copy" withinPortal>
            <ActionIcon
              size={18}
              variant="default"
              onClick={() => {
                navigator.clipboard
                  .writeText(String(hoverCopy.value ?? ''))
                  .catch(err => console.log(`hover-copy writeText ${err}`));
              }}
              aria-label="Copy cell value"
              style={{
                position: 'absolute',
                top: hoverCopy.top,
                left: hoverCopy.left,
                width: 20,
                height: 20,
                minHeight: 20,
                minWidth: 20,
                padding: 0,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                zIndex: 2
              }}
            >
              <TbCopy size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
    );
  }
);

OutputCanvasTable.displayName = 'OutputCanvasTable';

export default OutputCanvasTable;
